import React, { PropsWithChildren } from 'react';
import { makeStyles, Dialog } from '@material-ui/core';
import {
  Home,
  LayoutGrid,
  Puzzle,
  BookOpen,
  PlusCircle,
  MessageSquare,
  Layers,
  Menu,
  Users,
  FolderOpen,
  Shield,
  Bot,
  KeyRound,
  Receipt,
  FileText,
  BarChart3,
  Wallet,
  CreditCard,
} from 'lucide-react';
import { SearchModalProvider, useSearchModal } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import {
  Sidebar,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { UserAccessWrapper } from '../utils/UserAccessWrapper';
import { SidebarDivider } from './SidebarDivider';
import { TopNavigation, TOPNAV_HEIGHT } from './TopNavigation';
import { SearchModal } from '../search/SearchModal';

// Wrapper to make Lucide icons compatible with Backstage SidebarItem
const createSidebarIcon = (
  LucideIcon: React.FC<{ size?: number | string }>,
) => {
  const SidebarIcon = () => <LucideIcon size={20} />;
  return SidebarIcon;
};

const HomeIcon = createSidebarIcon(Home);
const CatalogIcon = createSidebarIcon(LayoutGrid);
const ApiIcon = createSidebarIcon(Puzzle);
const DocsIcon = createSidebarIcon(BookOpen);
const CreateIcon = createSidebarIcon(PlusCircle);
const FeedbackIcon = createSidebarIcon(MessageSquare);
const ExploreIcon = createSidebarIcon(Layers);
const GroupIcon = createSidebarIcon(Users);
const FolderIcon = createSidebarIcon(FolderOpen);
const AdminIcon = createSidebarIcon(Shield);
const AiAgentsIcon = createSidebarIcon(Bot);
const ProviderAccountsIcon = createSidebarIcon(KeyRound);
const BillingIcon = createSidebarIcon(Receipt);
const InvoiceIcon = createSidebarIcon(FileText);
const SpUsageIcon = createSidebarIcon(BarChart3);
const BillingOverviewIcon = createSidebarIcon(Wallet);
const TermOfPaymentIcon = createSidebarIcon(CreditCard);
const MenuIcon = () => <Menu size={20} />;

const INTERNAL_USER_VISIBILITY = 'internal' as const;

const ROUTES = {
  home: '/',
  catalog: 'catalog',
  feedback: 'feedback',
  apiDocs: 'api-docs',
  docs: 'docs',
  create: 'create',
  projOnboard: 'projects/onboarding',
  projects: 'projects',
  explore: 'explore',
  rbac: 'rbac',
  aiAgents: 'ai-agents',
  aiAgentsProviderAccounts: 'ai-agents/provider-accounts',
  billing: 'billing',
  billingOverview: 'billing/overview',
  billingSpUsage: 'billing/sp-usage',
  billingInvoice: 'billing/invoice',
  billingInvoices: 'billing/invoices',
  billingTermOfPayment: 'billing/term-of-payment',
} as const;

const LABELS = {
  home: 'Home',
  menu: 'Menu',
  catalog: 'Catalog',
  feedback: 'Feedback',
  myGroup: 'My Group',
  myGroups: 'My Groups',
  apis: 'APIs',
  docs: 'Docs',
  create: 'Create...',
  projOnboard: 'New Project',
  projects: 'Projects',
  explore: 'Explore',
  rbac: 'Administration',
  aiAgents: 'AI Agents',
  aiAgentsAgents: 'Agents',
  aiAgentsProviderAccounts: 'Provider Accounts',
  billing: 'Billing',
  billingOverview: 'Overview',
  spUsage: 'SP Usage',
  invoice: 'Invoice (Legacy)',
  invoices: 'Invoices',
  termOfPayment: 'Term of Payment',
} as const;

const useRootStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  content: {
    paddingTop: TOPNAV_HEIGHT,
    flex: 1,
  },
});

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const classes = useRootStyles();

  return (
    <SearchContextProvider>
      <SearchModalProvider>
        <div className={classes.wrapper}>
          <TopNavigation />
          <SearchModalDialog />
          <div className={classes.content}>
            <SidebarPageContent>{children}</SidebarPageContent>
          </div>
        </div>
      </SearchModalProvider>
    </SearchContextProvider>
  );
};

const SearchModalDialog = () => {
  const { state, toggleModal } = useSearchModal();

  return (
    <Dialog open={!state.hidden} onClose={toggleModal} fullWidth maxWidth="lg">
      <SearchModal toggleModal={toggleModal} />
    </Dialog>
  );
};

const SidebarPageContent = ({ children }: PropsWithChildren<{}>) => {
  return (
    <SidebarPage>
      <Sidebar>
        <SidebarGroup label={LABELS.menu} icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={HomeIcon} to={ROUTES.home} text={LABELS.home} />
          <SidebarItem
            icon={FolderIcon}
            to={ROUTES.projects}
            text={LABELS.projects}
          />
          <SidebarItem
            icon={CatalogIcon}
            to={ROUTES.catalog}
            text={LABELS.catalog}
          />
          <UserAccessWrapper exposeToUser={INTERNAL_USER_VISIBILITY}>
            <SidebarItem
              icon={FeedbackIcon}
              to={ROUTES.feedback}
              text={LABELS.feedback}
            />
          </UserAccessWrapper>
          <UserAccessWrapper exposeToUser={INTERNAL_USER_VISIBILITY}>
            <MyGroupsSidebarItem
              singularTitle={LABELS.myGroup}
              pluralTitle={LABELS.myGroups}
              icon={GroupIcon}
            />
          </UserAccessWrapper>
          <SidebarItem icon={ApiIcon} to={ROUTES.apiDocs} text={LABELS.apis} />
          <SidebarItem icon={DocsIcon} to={ROUTES.docs} text={LABELS.docs} />
          <SidebarItem
            icon={CreateIcon}
            to={ROUTES.create}
            text={LABELS.create}
          />
          <UserAccessWrapper exposeToUser={INTERNAL_USER_VISIBILITY}>
            <SidebarItem
              icon={ExploreIcon}
              to={ROUTES.explore}
              text={LABELS.explore}
            />
          </UserAccessWrapper>
          <UserAccessWrapper exposeToUser={INTERNAL_USER_VISIBILITY}>
            <SidebarItem icon={AdminIcon} to={ROUTES.rbac} text={LABELS.rbac} />
          </UserAccessWrapper>
          <UserAccessWrapper exposeToUser={INTERNAL_USER_VISIBILITY}>
            <SidebarItem
              icon={AiAgentsIcon}
              to={ROUTES.aiAgents}
              text={LABELS.aiAgents}
            >
              <SidebarSubmenu title={LABELS.aiAgents}>
                <SidebarSubmenuItem
                  title={LABELS.aiAgentsAgents}
                  to={ROUTES.aiAgents}
                  icon={AiAgentsIcon}
                />
                <SidebarSubmenuItem
                  title={LABELS.aiAgentsProviderAccounts}
                  to={ROUTES.aiAgentsProviderAccounts}
                  icon={ProviderAccountsIcon}
                />
              </SidebarSubmenu>
            </SidebarItem>
          </UserAccessWrapper>
          <UserAccessWrapper exposeToUser={INTERNAL_USER_VISIBILITY}>
            <SidebarItem icon={BillingIcon} text={LABELS.billing}>
              <SidebarSubmenu title={LABELS.billing}>
                <SidebarSubmenuItem
                  title={LABELS.billingOverview}
                  to={ROUTES.billingOverview}
                  icon={BillingOverviewIcon}
                />
                <SidebarSubmenuItem
                  title={LABELS.spUsage}
                  to={ROUTES.billingSpUsage}
                  icon={SpUsageIcon}
                />
                <SidebarSubmenuItem
                  title={LABELS.invoices}
                  to={ROUTES.billingInvoices}
                  icon={InvoiceIcon}
                />
                <SidebarSubmenuItem
                  title={LABELS.termOfPayment}
                  to={ROUTES.billingTermOfPayment}
                  icon={TermOfPaymentIcon}
                />
              </SidebarSubmenu>
            </SidebarItem>
          </UserAccessWrapper>
          {/* End global nav */}
          <SidebarDivider />
          <SidebarScrollWrapper>
            {/* Items in this group will be scrollable if they run out of space */}
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
